import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import Skeleton from "react-loading-skeleton";
import Network from "../../utils/Network";
import { ClegSupply, fetchClegPrice, fetchClegSupply } from "../../services/cleg-token";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { formatCompactNumber, formatNumber } from "../../i18n/formatters";
import "./login-stats.css";

// undefined while loading, null when the source did not answer.
type Loadable<T> = T | null | undefined;
// "markeplaceVolume" is how /statistics spells it.
type GameStatistics = { players: number; markeplaceVolume: number };

const percent = (ratio: number) =>
  ratio > 0 && ratio < 0.001
    ? `<${formatNumber(0.001, { style: "percent", maximumFractionDigits: 1 })}`
    : formatNumber(ratio, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });

function value<T>(data: Loadable<T>, render: (data: T) => React.ReactNode, width: number, missing: string) {
  if (data === undefined) return <Skeleton width={width} />;
  if (data === null) return <span className="login-stat-missing">{missing}</span>;
  return render(data);
}

type StatProps = { icon: string; title: string; delay: number; children: React.ReactNode };

function Stat({ icon, title, delay, children }: StatProps) {
  return (
    <motion.div className="login-stat" transition={{ duration: 0.3, delay }}
      initial={isMobile ? undefined : { y: 150, opacity: 0 }}
      animate={{ opacity: 1, y: 0 }} exit={{ y: 150, opacity: 0 }}>
      <img className="login-stat-icon" src={`/assets/images/icons/login/${icon}.png`} alt="" />
      <div className="login-stat-panel">
        <h2 className="login-stat-title">{title}</h2>
        {children}
      </div>
    </motion.div>
  );
}

export default function LoginStats() {
  const { t } = useAppTranslation("auth");
  const missing = t("auth:loginStats.unavailable");
  const [game, setGame] = useState<Loadable<GameStatistics>>();
  const [price, setPrice] = useState<Loadable<number>>();
  const [supply, setSupply] = useState<Loadable<ClegSupply>>();

  useEffect(() => {
    let cancelled = false;
    const settle = <T,>(set: (data: T | null) => void) => (data?: T | null) => {
      if (!cancelled) set(data || null);
    };
    new Network().CallApi<GameStatistics>({ method: "get", url: "/statistics" })
      .then(response => settle(setGame)(response.ok ? response.data : null))
      .catch(() => settle(setGame)());
    fetchClegPrice().catch(() => undefined).then(settle(setPrice));
    fetchClegSupply().catch(() => undefined).then(settle(setSupply));
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="login-stats">
      <Stat icon="players" title={t("auth:loginStats.players.title")} delay={0.1}>
        <div className="login-stat-value">
          {value(game, data => formatNumber(data.players), 90, missing)}
        </div>
        <p className="login-stat-note">{t("auth:loginStats.players.note")}</p>
      </Stat>

      <Stat icon="marketplace-volume" title={t("auth:loginStats.marketVolume.title")} delay={0.3}>
        <div className="login-stat-value">
          {value(game, data => <>
            {t("auth:loginStats.clegAmount", { amount: formatCompactNumber(data.markeplaceVolume) })}
            {price ? (
              <span className="login-stat-aside">
                {t("auth:loginStats.usdAmount", { amount: formatCompactNumber(data.markeplaceVolume * price) })}
              </span>
            ) : null}
          </>, 150, missing)}
        </div>
        <p className="login-stat-note">
          {t("auth:loginStats.marketVolume.note")}
          {price
            ? ` ${t("auth:loginStats.marketVolume.price", { price: formatNumber(price, { maximumSignificantDigits: 3 }) })}`
            : ""}
        </p>
      </Stat>

      <Stat icon="candle" title={t("auth:loginStats.burnt.title")} delay={0.5}>
        <div className="login-stat-value">
          {value(supply, data => <>
            {t("auth:loginStats.clegAmount", { amount: formatNumber(data.burnt) })}
            <span className="login-stat-aside">
              {t("auth:loginStats.burnt.share", {
                percent: percent(data.burnt / data.minted),
                minted: formatCompactNumber(data.minted),
              })}
            </span>
          </>, 180, missing)}
        </div>
        <p className="login-stat-note">{t("auth:loginStats.burnt.note")}</p>
      </Stat>
    </div>
  );
}
